/**
 * When an aeroplane stops being a going concern, and when the fight is over.
 *
 * Three ways to lose an aircraft, all of them already modelled elsewhere; this
 * only reads them:
 *
 * - **Wings gone** — `damage.wingsFailed`. `structuralLiftFactor` drops lift to
 *   a third and `damageRollBias` puts in an uncommanded roll, so the aircraft
 *   is not flyable; it goes down and the ground finishes it.
 * - **Fire** — the burning descent. `advanceFire` is eating the wings, so this
 *   ends the same way, just more slowly and more visibly.
 * - **Pilot dead** — shot in the cockpit, or bled out. Immediate.
 *
 * "Destroyed" is deliberately *not* the same as "out of control": an aircraft
 * with its wings off is a kill in every sense a scoring system cares about, but
 * it is still in the air and still worth drawing, so the two are separate
 * states and the encounter only ends on the first.
 *
 * **AD-014 — the death arc.** That separation was already right for wings and
 * fire, and it was already what made the encounter wait for a burning aeroplane
 * to come down. A dead pilot was the one path that skipped it: `!pilot.alive`
 * returned `destroyed` on the spot, which ended the fight with the victim still
 * at two thousand feet and popped the debrief over an aeroplane that had not
 * gone anywhere. That is now `outOfControl` like the others — a dead pilot's
 * aeroplane is still an aeroplane, still drawn, still burning, and still falling
 * — and `destroyed` is reserved for the wreck arriving.
 *
 * Kill *credit* does not move: it never keyed off this status. The shooter is
 * remembered in `CombatAircraft.lastAttackerId` at the moment the rounds land,
 * precisely so the destroying tick — several seconds later, with nobody firing
 * — can still name him.
 */

import { isWreck } from "../flight/derelict.ts";
import type { SimState } from "../flight/state.ts";
import type { FireState } from "./combat-damage.ts";

export const AIRCRAFT_STATUSES = ["flying", "outOfControl", "destroyed"] as const;

export type AircraftStatus = (typeof AIRCRAFT_STATUSES)[number];

export const ENCOUNTER_STATUSES = ["active", "playerDown", "enemyDown", "mutualDestruction"] as const;

export type EncounterStatus = (typeof ENCOUNTER_STATUSES)[number];

export interface StatusInput {
  readonly sim: SimState;
  readonly fire: FireState;
  readonly previous: AircraftStatus;
  /**
   * Seconds this aircraft has already spent out of control. Only the timeout
   * reads it, and only to stop a wreck holding the verdict open forever.
   * Omitted means "has not been falling", which is what every caller outside the
   * roster tick means.
   */
  readonly fallingSeconds?: number;
}

export function aircraftStatus(input: StatusInput): AircraftStatus {
  const { sim } = input;
  if (input.previous === "destroyed") return "destroyed";
  if (sim.crashed) return "destroyed";
  // An uncontrollable aircraft that reaches the ground at any speed is finished,
  // even if it arrived gently enough that `step` did not call it a crash.
  if (input.previous === "outOfControl" && sim.readout.grounded) return "destroyed";
  // The wreck has been falling long enough. Nothing physical happens here — it
  // is a promise to the player that the fight will be judged, kept when the
  // sky refuses to finish the job (AD-014).
  if (
    input.previous === "outOfControl" &&
    (input.fallingSeconds ?? 0) >= sim.config.tunables.wreckFallTimeoutSeconds
  ) {
    return "destroyed";
  }
  // `isWreck` rather than a second spelling of it. This line used to read
  // `!sim.pilot.alive || sim.damage.wingsFailed`, which is `isWreck`'s body
  // written out — and `lessons.md`'s doomed-descent entry is about exactly that
  // duplication drifting apart. AD-021's bailed pilot is the case that would
  // have drifted: he leaves an aeroplane nobody is flying, and only the helper
  // knows it.
  if (isWreck(sim.damage, sim.pilot) || input.fire.burning) return "outOfControl";
  return "flying";
}

export interface SideRoll {
  readonly playerAlive: boolean;
  readonly enemyAlive: boolean;
}

/** The encounter ends when one side has nothing left flying. */
export function encounterStatus(roll: SideRoll): EncounterStatus {
  if (!roll.playerAlive && !roll.enemyAlive) return "mutualDestruction";
  if (!roll.playerAlive) return "playerDown";
  if (!roll.enemyAlive) return "enemyDown";
  return "active";
}

export function isOver(status: EncounterStatus): boolean {
  return status !== "active";
}
